import { format } from 'date-fns';

export const MIN_DURATION_MINUTES = 30;

export const TIME_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export const DEFAULT_START_TIME = '00:00';
export const DEFAULT_END_TIME = '23:59';

export const toIsoDate = (d: Date | undefined): string =>
  d ? format(d, 'yyyy-MM-dd') : '';

/**
 * Combine a Date (date-only) with an HH:mm wall-clock string into a single Date.
 * Returns null if either input is missing or time is malformed.
 */
export const combineDateTime = (
  date: Date | undefined,
  time: string
): Date | null => {
  if (!date) {
    return null;
  }
  if (!TIME_REGEX.test(time)) {
    return null;
  }
  const [hh, mm] = time.split(':').map(Number);
  const combined = new Date(date);
  combined.setHours(hh ?? 0, mm ?? 0, 0, 0);
  return combined;
};

export interface ValidationMessages {
  startAfterEnd: string;
  startTimeAfterEnd: string;
  minDuration: string;
}

export const validateDateOrder = (
  startDate: Date | undefined,
  endDate: Date | undefined,
  messages: Pick<ValidationMessages, 'startAfterEnd'>
): string => {
  if (!startDate || !endDate) {
    return '';
  }
  const startDay = new Date(startDate);
  startDay.setHours(0, 0, 0, 0);
  const endDay = new Date(endDate);
  endDay.setHours(0, 0, 0, 0);
  if (endDay.getTime() < startDay.getTime()) {
    return messages.startAfterEnd;
  }
  return '';
};

export const validateTimeOrder = (
  startDate: Date | undefined,
  endDate: Date | undefined,
  startTime: string,
  endTime: string,
  messages: Pick<ValidationMessages, 'startTimeAfterEnd'>
): string => {
  if (!startTime || !endTime) {
    return '';
  }
  if (!TIME_REGEX.test(startTime) || !TIME_REGEX.test(endTime)) {
    return '';
  }
  if (startDate && endDate) {
    const start = combineDateTime(startDate, startTime);
    const end = combineDateTime(endDate, endTime);
    if (start && end && end.getTime() <= start.getTime()) {
      return messages.startTimeAfterEnd;
    }
    return '';
  }
  if (endTime <= startTime) {
    return messages.startTimeAfterEnd;
  }
  return '';
};

export const validateMinDuration = (
  startDate: Date | undefined,
  endDate: Date | undefined,
  startTime: string,
  endTime: string,
  messages: Pick<ValidationMessages, 'minDuration'>
): string => {
  if (!startDate || !endDate) {
    return '';
  }
  const start = combineDateTime(startDate, startTime || DEFAULT_START_TIME);
  const end = combineDateTime(endDate, endTime || DEFAULT_END_TIME);
  if (!start || !end) {
    return '';
  }
  const durationMinutes = (end.getTime() - start.getTime()) / 60000;
  if (durationMinutes > 0 && durationMinutes < MIN_DURATION_MINUTES) {
    return messages.minDuration;
  }
  return '';
};
